import type { Components, Theme } from '@mui/material/styles';

import { motion, radius, type ColorMode } from './tokens';

/**
 * Field padding, per size.
 *
 * Small is the default across the app: a label sitting above its field already
 * costs a line, and at medium the pair is tall enough that a form of six fields
 * stops fitting on a laptop screen.
 */
const FIELD_PADDING = { medium: '12px', small: '8.5px' } as const;

/**
 * An Autocomplete's field, padded like every other input: the root holds no
 * vertical padding of its own and the inner input carries the same padding a
 * plain TextField uses, so the two end up exactly as tall as each other.
 */
function autocompleteField(vertical: string) {
  return {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    '& .MuiAutocomplete-input': { padding: `${vertical} 0 ${vertical} 14px` },
  };
}

/**
 * Component overrides (spec §9.1). The aim is that nothing on screen reads as
 * stock Material: no ALL-CAPS buttons, no default elevation stack, borders
 * doing the work shadows would do in dark mode.
 */
export function buildComponents(mode: ColorMode): Components<Theme> {
  const isDark = mode === 'dark';

  return {
    MuiTypography: {
      defaultProps: {
        /**
         * `subtitle1` and `subtitle2` are labels — a display name on a card, a
         * field caption — not sections of the document.
         *
         * MUI maps both to `<h6>` by default, which puts an `h6` after an `h2`
         * on nearly every screen: axe flags the jump, and a screen-reader user
         * navigating by heading lands on a person's name as if it were a
         * chapter. Mapping them to `<p>` leaves the outline to real headings.
         */
        variantMapping: { subtitle1: 'p', subtitle2: 'p' },
      },
    },

    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        ':root': {
          colorScheme: mode,
        },
        html: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
        },
        body: {
          // Tabular figures stop vote counts from shifting width as they change.
          fontVariantNumeric: 'tabular-nums',
        },
        // Every interactive element gets the same visible focus ring. Browsers
        // only show it for keyboard users, which is what :focus-visible is for.
        '*:focus-visible': {
          outline: `2px solid ${theme.palette.text.primary}`,
          outlineOffset: 2,
          borderRadius: radius.sm,
        },
        // Spec §9.3: honour the reader's motion preference everywhere, not
        // per-component. One rule beats remembering it in thirty places.
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
        '::selection': {
          background: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(16,18,22,0.10)',
        },
      }),
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          // Buttons are rectangles with softened corners, not pills. Pills are
          // reserved for chips and segmented controls, where the shape is what
          // says "this is one of several".
          borderRadius: radius.sm,
          fontWeight: 600,
          transition: `background-color ${motion.fast}ms ${motion.easing}, border-color ${motion.fast}ms ${motion.easing}, color ${motion.fast}ms ${motion.easing}`,
        },
        sizeSmall: { padding: '4px 12px', fontSize: '0.875rem' },
        sizeMedium: { padding: '8px 16px' },
        sizeLarge: { padding: '11px 22px', fontSize: '1rem' },
      },
      variants: [
        // The three accent colours as first-class button colours, so the
        // composer can theme itself simply by passing color={type}.
        ...(['good', 'bad', 'ideal'] as const).map((accent) => ({
          props: { color: accent, variant: 'contained' as const },
          style: ({ theme }: { theme: Theme }) => ({
            backgroundColor: theme.palette[accent].solid,
            color: theme.palette[accent].contrastText,
            '&:hover': {
              backgroundColor: theme.palette[accent].solid,
              filter: 'brightness(1.08)',
            },
          }),
        })),
        ...(['good', 'bad', 'ideal'] as const).map((accent) => ({
          props: { color: accent, variant: 'outlined' as const },
          style: ({ theme }: { theme: Theme }) => ({
            borderColor: theme.palette[accent].border,
            color: theme.palette[accent].text,
            '&:hover': {
              borderColor: theme.palette[accent].main,
              backgroundColor: theme.palette[accent].softer,
            },
          }),
        })),
        ...(['good', 'bad', 'ideal'] as const).map((accent) => ({
          props: { color: accent, variant: 'text' as const },
          style: ({ theme }: { theme: Theme }) => ({
            color: theme.palette[accent].text,
            '&:hover': { backgroundColor: theme.palette[accent].softer },
          }),
        })),
      ],
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: radius.card,
          border: `1px solid ${theme.palette.divider}`,
          backgroundImage: 'none',
          boxShadow: isDark ? 'none' : theme.shadows[1],
        }),
      },
    },

    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: ({ theme }) => ({
          borderRadius: radius.card,
          borderColor: theme.palette.divider,
        }),
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radius.pill,
          fontWeight: 550,
          fontSize: '0.8125rem',
          height: 28,
        },
        sizeSmall: { height: 24, fontSize: '0.75rem' },
        label: { paddingInline: 10 },
        icon: { marginLeft: 8, marginRight: -4 },
      },
      variants: (['good', 'bad', 'ideal'] as const).flatMap((accent) => [
        {
          props: { color: accent, variant: 'filled' as const },
          style: ({ theme }: { theme: Theme }) => ({
            backgroundColor: theme.palette[accent].soft,
            color: theme.palette[accent].text,
            border: `1px solid ${theme.palette[accent].border}`,
            '& .MuiChip-icon': { color: theme.palette[accent].text },
          }),
        },
        {
          props: { color: accent, variant: 'outlined' as const },
          style: ({ theme }: { theme: Theme }) => ({
            borderColor: theme.palette[accent].border,
            color: theme.palette[accent].text,
            '& .MuiChip-icon': { color: theme.palette[accent].text },
          }),
        },
      ]),
    },

    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small' },
    },

    /**
     * Labels sit *above* their field rather than floating into its border.
     *
     * MUI's default is the notched outline: the label starts inside the input
     * and animates up into a gap in the border. It looks clever and it reads
     * badly — the label is only ever at full size once you have already typed
     * something, an empty field shows its label where the value will be, and
     * the notch makes every input a slightly different shape. A plain label on
     * its own line is always readable, always in the same place, and leaves the
     * placeholder free to say something useful ("Any town").
     *
     * Three pieces make it work: the label leaves the absolute-positioned flow,
     * it never transforms, and the outline's notch legend is removed so the
     * border closes up.
     */
    MuiInputLabel: {
      defaultProps: { shrink: true },
      styleOverrides: {
        root: ({ theme }) => ({
          position: 'relative',
          transform: 'none',
          maxWidth: '100%',
          marginBottom: 6,
          fontSize: '0.875rem',
          fontWeight: 500,
          color: theme.palette.text.secondary,
          // The label is a name, not a state: it should not change colour when
          // the field takes focus.
          '&.Mui-focused': { color: theme.palette.text.secondary },
          '&.Mui-error': { color: theme.palette.bad.text },
          '&.Mui-disabled': { color: theme.palette.text.disabled },
        }),
        outlined: { transform: 'none' },
        shrink: { transform: 'none' },
        // Every optional field says "(optional)" in its own label, so the
        // asterisk on all the others is a second way of saying the same thing.
        asterisk: { display: 'none' },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: radius.input,
          backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : theme.palette.background.paper,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.divider,
            transition: `border-color ${motion.fast}ms ${motion.easing}`,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.text.disabled,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 1,
            borderColor: theme.palette.text.primary,
          },
        }),
        // Closing the notch: without a legend the outline is a plain rectangle
        // again, which is what a label above the field needs.
        notchedOutline: { top: 0, '& legend': { display: 'none' } },
        // A multiline field pads its *root*, not its textarea; padding both is
        // what indents the first line of a bio away from every other field.
        input: {
          padding: `${FIELD_PADDING.medium} 14px`,
          '&.MuiInputBase-inputSizeSmall': { padding: `${FIELD_PADDING.small} 14px` },
        },
        multiline: {
          padding: `${FIELD_PADDING.medium} 14px`,
          '&.MuiInputBase-sizeSmall': { padding: `${FIELD_PADDING.small} 14px` },
          '& textarea': { padding: 0 },
        },
      },
    },

    /**
     * An Autocomplete is a select as far as the reader is concerned, so it has
     * to be the same height as one.
     *
     * MUI pads the *root* and leaves the inner input nearly flush, which is a
     * different sum from the plain `12px 14px` every other field uses: a
     * country picker came out 39px tall next to a 46px text field, and in the
     * filter column the two sat one above the other looking misaligned.
     *
     * Only the vertical and left padding are reset — `paddingRight` is what
     * keeps the value from running underneath the dropdown arrow, and MUI sizes
     * it from how many adornments the field actually has.
     *
     * Stated twice, once per size, because MUI's own rule is
     * `.MuiOutlinedInput-root.MuiInputBase-sizeSmall` and a single-class
     * override loses to it on specificity.
     */
    MuiAutocomplete: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        inputRoot: {
          '&.MuiOutlinedInput-root': autocompleteField(FIELD_PADDING.medium),
          '&.MuiOutlinedInput-root.MuiInputBase-sizeSmall': autocompleteField(FIELD_PADDING.small),
        },
      },
    },

    /**
     * The segmented control: one track, the selected segment lifted out of it.
     *
     * Pills here rather than the softened rectangles buttons use — the shape is
     * what says "one of these", and the group reads as a single control instead
     * of three separate buttons sitting next to each other.
     */
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.action.selected,
          borderRadius: radius.pill,
          padding: 3,
          gap: 2,
        }),
        grouped: {
          border: 0,
          borderRadius: `${radius.pill}px !important`,
        },
      },
    },

    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.8125rem',
          padding: '4px 14px',
          color: theme.palette.text.secondary,
          transition: `background-color ${motion.fast}ms ${motion.easing}, color ${motion.fast}ms ${motion.easing}`,
          '&:hover': { backgroundColor: 'transparent', color: theme.palette.text.primary },
          '&.Mui-selected': {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            boxShadow: theme.shadows[1],
            '&:hover': { backgroundColor: theme.palette.background.paper },
          },
        }),
      },
    },

    MuiFormHelperText: {
      styleOverrides: { root: { marginLeft: 2, fontSize: '0.8125rem' } },
    },

    /**
     * The scrim behind a dialog or drawer: dark enough to put the page out of
     * play, blurred so what is behind reads as depth rather than as competing
     * content.
     *
     * The invisible variant is exempted by hand — Menu, Popover and Select all
     * mount a backdrop purely to catch the click that closes them, and dimming
     * the screen every time someone opens a select would be absurd.
     */
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          '&.MuiBackdrop-invisible': {
            backgroundColor: 'transparent',
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
          },
        },
      },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'transparent' },
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: isDark ? 'rgba(15, 17, 21, 0.72)' : 'rgba(250, 250, 248, 0.72)',
          backdropFilter: 'blur(12px) saturate(160%)',
          WebkitBackdropFilter: 'blur(12px) saturate(160%)',
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundImage: 'none',
          boxShadow: 'none',
        }),
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 44 },
        indicator: ({ theme }) => ({
          height: 2,
          borderRadius: 2,
          backgroundColor: theme.palette.text.primary,
        }),
      },
    },

    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'none',
          minHeight: 44,
          fontWeight: 600,
          fontSize: '0.9375rem',
          color: theme.palette.text.secondary,
          '&.Mui-selected': { color: theme.palette.text.primary },
        }),
      },
    },

    MuiSkeleton: {
      defaultProps: { animation: 'wave' },
      styleOverrides: {
        root: { borderRadius: radius.sm },
        rounded: { borderRadius: radius.card },
      },
    },

    MuiTooltip: {
      defaultProps: { arrow: true, enterDelay: 400 },
      styleOverrides: {
        tooltip: ({ theme }) => ({
          fontSize: '0.8125rem',
          borderRadius: radius.sm,
          backgroundColor: theme.palette.text.primary,
          color: theme.palette.background.default,
          paddingBlock: 6,
        }),
        arrow: ({ theme }) => ({ color: theme.palette.text.primary }),
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: radius.sm },
      },
    },

    MuiDivider: {
      styleOverrides: { root: ({ theme }) => ({ borderColor: theme.palette.divider }) },
    },

    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          textDecorationColor: theme.palette.text.disabled,
          fontWeight: 550,
        }),
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: radius.card, fontSize: '0.9375rem' },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: radius.card,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[3],
        }),
      },
    },

    MuiListItemButton: {
      styleOverrides: { root: { borderRadius: radius.input } },
    },
  };
}
